// src/context/ThemeContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const THEMES = {
  dark: {
    bg:      '#07080c',
    bg2:     '#0c0f18',
    bg3:     '#111520',
    surf:    '#161c2a',
    border:  'rgba(255,255,255,.07)',
    borderH: 'rgba(255,255,255,.13)',
    white:   '#dce6fa',
    muted:   'rgba(220,230,250,.45)',
    dim:     'rgba(220,230,250,.22)',
    input:   '#0c0f18',
    inputBorder: 'rgba(255,255,255,.1)',
    card:    'rgba(255,255,255,.025)',
    shadow:  'rgba(0,0,0,.4)',
    // accents unchanged
    cyan:    '#00c8f0',
    blue:    '#4d8fff',
    green:   '#2ecc71',
    red:     '#e74c3c',
    yellow:  '#f1c40f',
    purple:  '#b78cff',
    orange:  '#e67e22',
    teal:    '#1abc9c',
  },
  light: {
    bg:      '#f5f7fa',
    bg2:     '#ffffff',
    bg3:     '#f0f2f7',
    surf:    '#e8ebf2',
    border:  'rgba(0,0,0,.09)',
    borderH: 'rgba(0,0,0,.18)',
    white:   '#1a1d2e',        // text — dark on light
    muted:   'rgba(26,29,46,.55)',
    dim:     'rgba(26,29,46,.35)',
    input:   '#ffffff',
    inputBorder: 'rgba(0,0,0,.15)',
    card:    'rgba(0,0,0,.03)',
    shadow:  'rgba(0,0,0,.08)',
    // slightly muted accents for light background readability
    cyan:    '#0099bb',
    blue:    '#2563eb',
    green:   '#16a34a',
    red:     '#dc2626',
    yellow:  '#b45309',
    purple:  '#7c3aed',
    orange:  '#c2410c',
    teal:    '#0f766e',
  },
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('arhat_theme') || 'dark';
  });

  const T = THEMES[theme];

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('arhat_theme', next);
  };

  // Apply to <html> for global CSS variable access
  useEffect(() => {
    const root = document.documentElement;
    const t    = THEMES[theme];
    root.style.setProperty('--bg',      t.bg);
    root.style.setProperty('--bg2',     t.bg2);
    root.style.setProperty('--bg3',     t.bg3);
    root.style.setProperty('--surf',    t.surf);
    root.style.setProperty('--border',  t.border);
    root.style.setProperty('--borderH', t.borderH);
    root.style.setProperty('--white',   t.white);
    root.style.setProperty('--muted',   t.muted);
    root.style.setProperty('--dim',     t.dim);
    root.style.setProperty('--cyan',    t.cyan);
    root.style.setProperty('--blue',    t.blue);
    root.style.setProperty('--green',   t.green);
    root.style.setProperty('--red',     t.red);
    root.style.setProperty('--yellow',  t.yellow);
    root.style.setProperty('--purple',  t.purple);
    root.style.setProperty('--orange',  t.orange);
    root.style.setProperty('--teal',    t.teal);
    root.setAttribute('data-theme', theme);
    document.body.style.background = t.bg;
    document.body.style.color      = t.white;
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, T, toggleTheme, isDark: theme === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);